import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { collection, getDocs, query, where } from 'firebase/firestore'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import {
  shouldPromptDiscard,
  getCurrencyAffix,
  formatCommentRelativeTime,
  DETAIL_TABS,
  SECTION_TO_TAB,
  type DetailTab,
  type DisplayChecklistComment,
} from './MemberChecklistDetailPage.helpers'
import { useNls } from '../../nls/nlsProvider'
import { DependencyLockChip, MissedPeriodsChip } from '../Common'
import { usePushBackHandler } from '../../context/BackStackContext'
import { firestoreDb } from '../../services/firebase'
import { getPublicProfiles, type PublicProfile } from '../../services/publicProfileService'
import { subscribeHelperStore } from '../../services/helper/helperStore'
import { retryMutation } from '../../services/helper/syncWorker'
import type { ChecklistCommentPayload, HelperMutationRecord } from '../../services/helper/helperOutboxDb'
import { useAuth } from '../../context/useAuth'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useAutoresizeTextarea } from '../../hooks/useAutoresizeTextarea'
import { useMentionOnAt } from '../../hooks/useMentionOnAt'
import { useDesignAssets } from '../../hooks/useDesignAssets'
import { useFormDefinitions } from '../../hooks/useFormDefinitions'
import { useEditLock } from '../../hooks/useEditLock'
import { isManualSaveField } from '../../services/checklistEditFieldPolicy'
import { useTimeline } from '../../context/useTimeline'
import { useToast } from '../../context/useToast'
import { useWorkspace } from '../../context/useWorkspace'
import { ItemTimerButton } from '../Timesheets/ItemTimerButton'
import { ItemTimeDisplay } from '../Timesheets/ItemTimeDisplay'
import { ItemTimeProvider } from '../Timesheets/ItemTimeProvider'
import {
  submitHelperComment,
  submitHelperProof,
  subscribeChecklistComments,
  subscribeChecklistProofs,
  type HelperChecklistComment,
  type HelperChecklistProof,
} from '../../services/helper/helperChecklistDetailsService'
import {
  assignChecklistItem,
  updateChecklistFields,
} from '../../services/checklistMutationService'
import { updateChecklistWorkflowStatus } from '../../services/checklistStatusWriter'
import { applyStripboardDelta, ChecklistBlockerError } from '../../services/stripboardDeltaService'
import { subscribeChecklistItemProjection } from '../../services/checklistReader'
import { buildLockGraph, resolveLock } from '../../services/checklistLockService'
import { useChecklistLockState, type LockTarget } from '../../hooks/useChecklistLockState'
import {
  getChecklistDependencyBlockState,
  normalizeChecklistBlockedTodoPolicy,
} from '../../utils/checklistBlockedTodoPolicy'
import { parseWorkspaceRole, roleCanPerform } from '../../utils/workspacePermissions'
import { getWorkspaceOwnerIds } from '../../utils/workspaceOwners'
import { filterWorkflowStatusOptions } from '../../utils/checklistNodeMeta'
import {
  canPickInDropdown,
  evaluateBlockers,
  STATUS_META,
  type BlockerEntry,
} from '../../services/checklistStatusMachine'
import { ctaSectionFor, type BlockerCtaSection } from '../../types/checklistBlockerCodes'
import { StatusBlockerBanner } from '../DrawingBoard/StatusBlockerBanner'
import {
  buildMobileChecklistOccurrenceOptions,
  getMobileRecurringMode,
} from '../ChecklistBoard/mobileChecklistOccurrenceUtils'
import { resolveDefaultBreadcrumb } from '../../utils/checklistBreadcrumb'
import { subscribeToBookPages } from '../../services/checklistBookService'
import { subscribeWorkspaceFiles } from '../../services/storageHubService'
import { bookIdFor, type ChecklistBookPage } from '../../types/checklistBooks'
import { currentOccurrenceKeyForItem } from '../../services/recurringChecklistService'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'
import { useWorkspaceDraft } from '../../hooks/useWorkspaceDraft'
import { registerSwitchGuard } from '../../services/workspaceSwitch/switchGuards'
import type { ChecklistItem, ChecklistMemberOption, ChecklistWorkflowStatus } from '../../types/schema'
import { Button, HiddenFileInput, IconButton, Pressable, Select, StatusChip, Textarea, TextInput } from '../Common'
import { AppDialog } from '../Common/AppDialog'
import { SettingsRadioList } from '../Common/SettingsRadioList'
import { BottomSheet } from '../Common/BottomSheet'
import { EmptyHubState } from '../Common/EmptyHubState'
import { SheetSelect } from '../Common/SheetSelect'
import { DatePicker } from '../Common/DatePicker'
import { ChecklistFieldPassControl } from '../FieldPass/ChecklistFieldPassControl'
import { ChecklistAttachedDesignsSection } from '../Forms/ChecklistAttachedDesignsSection'
import { ChecklistAttachedFormsSection } from '../Forms/ChecklistAttachedFormsSection'
import { WorkspaceItemPicker, type WorkspaceItemReference } from '../Common/WorkspaceItemPicker'

const getInitialDraft = (item: ChecklistItem) => ({
  title: item.title,
  description: item.description ?? '',
  completionCriteria: item.completionCriteria ?? '',
  weightage: String(item.weightage ?? 1),
  priority: String(item.priority ?? 1),
  complexityStars: String(item.complexityStars ?? 3),
  urgencyStars: String(item.urgencyStars ?? 3),
  importanceStars: String(item.importanceStars ?? 3),
  estimatedHours: item.estimatedHours == null ? '' : String(item.estimatedHours),
  estimatedCost: item.estimatedCost == null ? '' : String(item.estimatedCost),
  actualCost: item.actualCost == null ? '' : String(item.actualCost),
  currencyCode: item.currencyCode ?? 'USD',
  scheduleStartDate: toInputDate(item.scheduleStartDate ?? item.startDate),
  scheduleEndDate: toInputDate(item.scheduleEndDate ?? item.endDate),
  scheduleTimeFrom: item.scheduleTimeFrom ?? '',
  scheduleTimeTo: item.scheduleTimeTo ?? '',
  countdownHours: item.countdownHours == null ? '' : String(item.countdownHours),
  delayDurationHours: item.delayDurationHours == null ? '' : String(item.delayDurationHours),
})

const CHECKLIST_STATUSES: ChecklistWorkflowStatus[] = [
  'CREATED',
  'ASSIGNED',
  'ACCEPTED',
  'ONGOING',
  'ASSESSING',
  'COMPLETED',
  'NOT_REQUIRED',
  'POSTPONED',
]

const STATUS_LABEL: Record<ChecklistWorkflowStatus, string> = {
  CREATED: 'Created',
  ASSIGNED: 'Assigned',
  ACCEPTED: 'Accepted',
  ONGOING: 'Ongoing',
  ASSESSING: 'Assessing',
  COMPLETED: 'Completed',
  NOT_REQUIRED: 'Not Required',
  POSTPONED: 'Postponed',
}

const STATUS_TONE_CLASS: Record<ChecklistWorkflowStatus, string> = {
  CREATED: 'statusToneCreated',
  ASSIGNED: 'statusToneAssigned',
  ACCEPTED: 'statusToneAccepted',
  ONGOING: 'statusToneOngoing',
  ASSESSING: 'statusToneAssessing',
  COMPLETED: 'statusToneCompleted',
  NOT_REQUIRED: 'statusToneNotRequired',
  POSTPONED: 'statusTonePostponed',
}

const toInputDate = (value?: string | null) => value ? value.slice(0, 10) : ''
const toOptionalNumber = (value: string) => {
  if (!value.trim()) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const clampRating = (value: string, fallback = 3) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.max(1, Math.min(5, Math.round(parsed)))
}

const normalizeChecklistMemberRole = (role: unknown): ChecklistMemberOption['role'] =>
  parseWorkspaceRole(role) ?? 'VIEWER'

const resolveChecklistMemberDisplayName = (
  membershipData: Record<string, unknown>,
  profile: PublicProfile | Record<string, unknown> | undefined | null,
  fallback: string,
): string => {
  if (profile && typeof profile === 'object') {
    const preferences = (profile as Record<string, unknown>).displayPreferences
    if (preferences && typeof preferences === 'object') {
      const computedName = (preferences as { computedName?: unknown }).computedName
      if (typeof computedName === 'string' && computedName.trim()) return computedName.trim()
    }
  }
  const candidates = [
    profile?.displayName,
    membershipData.displayName,
    membershipData.name,
    profile?.username,
    membershipData.phone,
    fallback,
  ]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }
  return 'Workspace member'
}

const RatingInput = ({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string
  value: string
  disabled: boolean
  onChange: (value: string) => void
}) => {
  const current = clampRating(value)
  return (
    <div className="memberRatingField">
      <span>{label}</span>
      <div role="radiogroup" aria-label={label}>
        {[1, 2, 3, 4, 5].map((rating) => (
          <Pressable
            key={rating}
            disabled={disabled}
            aria-label={`${label} ${rating}`}
            aria-checked={current === rating}
            className={rating <= current ? 'isActive' : undefined}
            onClick={() => onChange(String(rating))}
          >
            {rating <= current ? <StarRoundedIcon fontSize="inherit" /> : <StarBorderRoundedIcon fontSize="inherit" />}
          </Pressable>
        ))}
      </div>
    </div>
  )
}

const ChecklistDetailHero = ({
  breadcrumb,
  status,
  onBack,
  onSubtitleClick,
  trailing,
}: {
  breadcrumb: string
  status: string
  onBack: () => void
  onSubtitleClick?: () => void
  trailing?: React.ReactNode
}) => (
  // TODO(app-consistency-pass): hoist subtitle + backAction into shared MemberHeroHeader.
  <header className="memberChecklistDetailHero">
    <IconButton variant="tertiary" size="md" className="memberChecklistDetailHero__back" label="Back" icon={<ChevronLeftRoundedIcon fontSize="small" />} onClick={onBack} />
    <div className="memberChecklistDetailHero__copy">
      <span className="memberEyebrow">Checklist</span>
      {onSubtitleClick ? (
        <Pressable
          className="memberChecklistDetailHero__titleBtn"
          onClick={onSubtitleClick}
          title={breadcrumb}
        >
          <h2>{breadcrumb}</h2>
        </Pressable>
      ) : (
        <h2 title={breadcrumb}>{breadcrumb}</h2>
      )}
    </div>
    <div className="memberChecklistDetailHero__actions">
      <span className="memberChecklistDetailHero__status">{status}</span>
      {trailing}
    </div>
  </header>
)

export const MemberChecklistDetailPage = ({
  item: baseItem,
  pendingStatus: initialPendingStatus,
  initialSection,
  initialOccurrenceKey,
  onClose,
  onOpenWorkspaceItem,
}: {
  item: ChecklistItem
  pendingStatus?: ChecklistWorkflowStatus | null
  initialSection?: BlockerCtaSection | null
  initialOccurrenceKey?: string | null
  onClose: () => void
  // Navigation is caller-provided so the page stays decoupled from AppMember's router.
  // Desktop/console callers that don't wire this render rows as read-only info.
  onOpenWorkspaceItem?: (reference: WorkspaceItemReference) => void
}) => {
  const { currentUser, userProfile } = useAuth()
  const displayName = useDisplayName(userProfile ?? currentUser)
  const { elements, checklistItems, canPerformOnNode } = useTimeline()
  const { activeWorkspaceId, activeRole, canPerform, workspaces } = useWorkspace()
  const { showToast } = useToast()
  const { t } = useNls()
  const fieldPassOnNotice = useCallback(
    (message: string, tone?: 'success' | 'error') => showToast(message, tone === 'error' ? 'error' : 'success'),
    [showToast],
  )
  const recurringMode = getMobileRecurringMode(baseItem)
  const [selectedOccurrenceKey, setSelectedOccurrenceKey] = useState<string | null>(() => {
    if (initialOccurrenceKey) return initialOccurrenceKey
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      const day = params.get('day')
      const week = params.get('week')
      if (day) return day
      if (week) return week
    }
    return null
  })
  const [occurrencePages, setOccurrencePages] = useState<ChecklistBookPage[]>([])

  const { timeZone, todayIso, weekKey } = useWorkspaceDay(baseItem.workspaceId)

  const occurrenceOptions = useMemo(
    () => buildMobileChecklistOccurrenceOptions(baseItem, occurrencePages, new Date(), undefined, timeZone),
    [baseItem, occurrencePages, timeZone],
  )

  const effectiveSelectedOccurrenceKey = useMemo(() => {
    if (!recurringMode) return null
    if (selectedOccurrenceKey && occurrenceOptions.some((option) => option.key === selectedOccurrenceKey)) {
      return selectedOccurrenceKey
    }
    return occurrenceOptions.find((option) => option.kind === 'current')?.key ?? occurrenceOptions[0]?.key ?? currentOccurrenceKeyForItem(baseItem, new Date(), timeZone)
  }, [baseItem, occurrenceOptions, recurringMode, selectedOccurrenceKey, timeZone])

  const [projectedItem, setProjectedItem] = useState<ChecklistItem | null>(null)
  const [prevBaseItemId, setPrevBaseItemId] = useState(baseItem.id)
  if (baseItem.id !== prevBaseItemId) {
    setPrevBaseItemId(baseItem.id)
    setProjectedItem(null)
    setSelectedOccurrenceKey(null)
  }

  const item = (recurringMode ? projectedItem : null) ?? baseItem
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId || workspace.id === item.workspaceId)
  const [draft, setDraft] = useState(() => getInitialDraft(baseItem))
  const [workspaceMembers, setWorkspaceMembers] = useState<ChecklistMemberOption[]>([])
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(item.assigneeId ?? '')
  const [pendingAssignee, setPendingAssignee] = useState<{ assignee: ChecklistMemberOption | null } | null>(null)
  const [notNeededScopeDialogOpen, setNotNeededScopeDialogOpen] = useState(false)
  const [notNeededScopeChoice, setNotNeededScopeChoice] = useState<'THIS_PERIOD' | 'FROM_PERIOD'>('THIS_PERIOD')
  const commentDraftKey = item?.id ? `draft.checklistComment.${item.id}` : ''
  const [commentDraft, setCommentDraft, clearCommentDraft] = useWorkspaceDraft(commentDraftKey)
  const [comments, setComments] = useState<HelperChecklistComment[]>([])
  const [commentsError, setCommentsError] = useState<string | null>(null)
  const [outboxMutations, setOutboxMutations] = useState<{
    pending: HelperMutationRecord[]
    failed: HelperMutationRecord[]
  }>({ pending: [], failed: [] })
  const [proofs, setProofs] = useState<HelperChecklistProof[]>([])
  const [busy, setBusy] = useState(false)
  const initialTab: DetailTab = initialSection && SECTION_TO_TAB[initialSection] ? SECTION_TO_TAB[initialSection] : 'overview'
  const [activeDetailTab, setActiveDetailTab] = useState<DetailTab>(initialTab)
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
  const proofFileInputRef = useRef<HTMLInputElement | null>(null)

  const focusTabAt = (index: number) => {
    const nextTab = tabRefs.current[index]
    nextTab?.focus()
  }

  const handleTabKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (DETAIL_TABS.length === 0) return
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      focusTabAt((index + 1) % DETAIL_TABS.length)
      return
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      focusTabAt((index - 1 + DETAIL_TABS.length) % DETAIL_TABS.length)
      return
    }
    if (event.key === 'Home') {
      event.preventDefault()
      focusTabAt(0)
      return
    }
    if (event.key === 'End') {
      event.preventDefault()
      focusTabAt(DETAIL_TABS.length - 1)
      return
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      selectTab(DETAIL_TABS[index].key)
    }
  }

  const [visitedTabs, setVisitedTabs] = useState<Set<DetailTab>>(() => new Set(['overview', initialTab]))
  if (!visitedTabs.has(activeDetailTab)) {
    setVisitedTabs(new Set([...visitedTabs, activeDetailTab]))
  }

  const highlightAndScrollSection = useCallback((section: BlockerCtaSection) => {
    window.setTimeout(() => {
      const el = document.getElementById(`mobile-checklist-section-${section}`)
      if (el) {
        el.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
        el.classList.remove('memberSectionHighlight')
        void el.offsetWidth
        el.classList.add('memberSectionHighlight')
        window.setTimeout(() => {
          el.classList.remove('memberSectionHighlight')
        }, 2000)
      }
    }, 60)
  }, [])

  const selectTab = useCallback((tab: DetailTab) => {
    setActiveDetailTab(tab)
    setVisitedTabs((prev) => (prev.has(tab) ? prev : new Set([...prev, tab])))
  }, [setActiveDetailTab, setVisitedTabs])

  const jumpToSection = useCallback((section: BlockerCtaSection) => {
    const targetTab = SECTION_TO_TAB[section]
    if (targetTab) {
      setActiveDetailTab(targetTab)
      setVisitedTabs((prev) => (prev.has(targetTab) ? prev : new Set([...prev, targetTab])))
    }
    highlightAndScrollSection(section)
  }, [highlightAndScrollSection, setActiveDetailTab, setVisitedTabs])

  const [prevInitialSection, setPrevInitialSection] = useState(initialSection)
  if (initialSection !== prevInitialSection) {
    setPrevInitialSection(initialSection)
    if (initialSection && SECTION_TO_TAB[initialSection]) {
      const targetTab = SECTION_TO_TAB[initialSection]
      setActiveDetailTab(targetTab)
      setVisitedTabs((prev) => (prev.has(targetTab) ? prev : new Set([...prev, targetTab])))
    }
  }

  useEffect(() => {
    if (initialSection) {
      highlightAndScrollSection(initialSection)
    }
  }, [highlightAndScrollSection, initialSection])
  const [crumbSheetOpen, setCrumbSheetOpen] = useState(false)
  const [pickerState, setPickerState] = useState<{ field: 'description' | 'completionCriteria' | 'comment'; multi?: boolean } | null>(null)
  const [fieldAttachments, setFieldAttachments] = useState<Record<'description' | 'completionCriteria' | 'comment', WorkspaceItemReference[]>>({ description: [], completionCriteria: [], comment: [] })
  const [attachedProvisionIds, setAttachedProvisionIds] = useState(() => ({
    forms: item.attachedFormIds ?? [],
    designs: item.attachedDesignIds ?? [],
    uploads: item.attachedUploadIds ?? [],
    links: item.attachedLinkIds ?? [],
  }))
  const [workspaceFiles, setWorkspaceFiles] = useState<Array<{ id: string; fileName?: string; sizeBytes?: number; mimeType?: string }>>([])
  const formsPage = useFormDefinitions({ workspaceId: item.workspaceId, lifecycleStatus: 'ACTIVE', search: '', sort: 'NEWEST', cursor: null })
  const designsPage = useDesignAssets(item.workspaceId, { status: 'COMPLETED' })
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null)
  const completionCriteriaRef = useRef<HTMLTextAreaElement | null>(null)
  const commentRef = useRef<HTMLTextAreaElement | null>(null)
  useAutoresizeTextarea(descriptionRef)
  useAutoresizeTextarea(completionCriteriaRef)
  useAutoresizeTextarea(commentRef)
  const openPickerForDescription = useCallback(() => setPickerState({ field: 'description', multi: false }), [setPickerState])
  const openPickerForCompletionCriteria = useCallback(() => setPickerState({ field: 'completionCriteria', multi: false }), [setPickerState])
  const openPickerForComment = useCallback(() => setPickerState({ field: 'comment', multi: false }), [setPickerState])
  const onDescriptionMention = useMentionOnAt(descriptionRef, openPickerForDescription)
  const onCompletionCriteriaMention = useMentionOnAt(completionCriteriaRef, openPickerForCompletionCriteria)
  const onCommentMention = useMentionOnAt(commentRef, openPickerForComment)

  useEffect(() => {
    if (!currentUser?.uid) return
    return subscribeWorkspaceFiles(item.workspaceId, currentUser.uid, false, setWorkspaceFiles)
  }, [currentUser?.uid, item.workspaceId])

  // 3b — Scroll the active tab button into view whenever the active tab
  // changes (covers both user taps and jumpToSection deep-links). The tab
  // strip is overflow-x: auto; without this the Proof tab sits off-screen
  // and the user has no affordance that it exists.
  useEffect(() => {
    const btn = document.getElementById(`tab-${activeDetailTab}`)
    btn?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
  }, [activeDetailTab])

  useEffect(() => {
    if (!recurringMode) return
    return subscribeToBookPages(bookIdFor(baseItem.workspaceId, recurringMode), setOccurrencePages)
  }, [baseItem.workspaceId, recurringMode])

  useEffect(() => {
    if (!recurringMode || !effectiveSelectedOccurrenceKey) {
      return
    }
    return subscribeChecklistItemProjection(
      { itemId: baseItem.id, pageKey: effectiveSelectedOccurrenceKey },
      setProjectedItem,
    )
  }, [baseItem.id, effectiveSelectedOccurrenceKey, recurringMode])



  useEffect(() => {
    let cancelled = false
    const loadMembers = async () => {
      const membershipSnapshot = await getDocs(query(
        collection(firestoreDb, 'workspace_members'),
        where('workspaceId', '==', item.workspaceId),
      ))
      const memberRows = membershipSnapshot.docs
        .map((membershipDoc) => membershipDoc.data() as Record<string, unknown>)
        .filter((data) => typeof data.status !== 'string' || data.status === 'ACTIVE')

      const memberUids = memberRows
        .map((data) => typeof data.uid === 'string' ? data.uid : typeof data.userId === 'string' ? data.userId : '')
        .filter(Boolean)

      const allUids = [...memberUids]
      const ownerIds = getWorkspaceOwnerIds(activeWorkspace)
      for (const ownerId of ownerIds) {
        if (!allUids.includes(ownerId)) {
          allUids.push(ownerId)
        }
      }

      const publicProfiles = await getPublicProfiles(allUids)

      const identities = memberRows.map((data) => {
        const userId = typeof data.uid === 'string' ? data.uid : typeof data.userId === 'string' ? data.userId : ''
        if (!userId) return null
        const profile = publicProfiles.get(userId)
        const email = typeof data.email === 'string'
          ? data.email
          : typeof data.user_email === 'string'
            ? data.user_email
            : ''
        return {
          userId,
          displayName: resolveChecklistMemberDisplayName(data, profile, email),
          email,
          role: normalizeChecklistMemberRole(data.role),
        } satisfies ChecklistMemberOption
      })

      for (const ownerId of ownerIds) {
        if (!identities.some((member) => member?.userId === ownerId)) {
          const ownerProfile = publicProfiles.get(ownerId)
          identities.push({
            userId: ownerId,
            displayName: ownerProfile?.displayName || 'Workspace admin',
            email: '',
            role: 'OWNER',
          })
        }
      }
      if (!cancelled) {
        const validMembers: ChecklistMemberOption[] = []
        for (const member of identities) {
          if (member) validMembers.push(member)
        }
        validMembers.sort((left, right) => left.displayName.localeCompare(right.displayName))
        setWorkspaceMembers(validMembers)
      }
    }
    void loadMembers().catch((error) => {
      console.error('[MemberChecklistDetailPage] member load failed', error)
      if (!cancelled) setWorkspaceMembers([])
    })
    return () => { cancelled = true }
  }, [activeWorkspace, item.workspaceId])

  useEffect(() => {
    const unsub = subscribeChecklistComments(
      item.id,
      item.workspaceId,
      (loaded) => {
        setComments(loaded)
        setCommentsError(null)
      },
      (err) => {
        console.error('[MemberChecklistDetailPage] subscribeChecklistComments error', err)
        setCommentsError(err.message || 'Could not load comments.')
      },
    )
    return () => {
      unsub()
      setCommentsError(null)
    }
  }, [item.id, item.workspaceId])

  useEffect(() => {
    return subscribeHelperStore((snapshot) => {
      const itemPending = snapshot.pending.filter(
        (m) => m.type === 'CHECKLIST_COMMENT' && (m.payload as ChecklistCommentPayload).checklistItemId === item.id,
      )
      const itemFailed = snapshot.failed.filter(
        (m) => m.type === 'CHECKLIST_COMMENT' && (m.payload as ChecklistCommentPayload).checklistItemId === item.id,
      )
      setOutboxMutations({ pending: itemPending, failed: itemFailed })
    })
  }, [item.id])

  const displayComments = useMemo<DisplayChecklistComment[]>(() => {
    const activePending = outboxMutations.pending.filter((mutation) => {
      const payload = mutation.payload as ChecklistCommentPayload
      return !comments.some((c) => c.body === payload.body && c.actorId === payload.actorId)
    })

    const pendingComments: DisplayChecklistComment[] = activePending.map((mutation) => {
      const payload = mutation.payload as ChecklistCommentPayload
      return {
        id: `pending-${mutation.id}`,
        body: payload.body,
        actorId: payload.actorId,
        actorName: payload.actorName,
        createdAt: mutation.createdAt,
        editedAt: null,
        deletedAt: null,
        deletedBy: null,
        userAvatarUrl: null,
        attachments: [],
        isPending: true,
        mutationId: mutation.id,
      }
    })

    const failedComments: DisplayChecklistComment[] = outboxMutations.failed.map((mutation) => {
      const payload = mutation.payload as ChecklistCommentPayload
      return {
        id: `failed-${mutation.id}`,
        body: payload.body,
        actorId: payload.actorId,
        actorName: payload.actorName,
        createdAt: mutation.createdAt,
        editedAt: null,
        deletedAt: null,
        deletedBy: null,
        userAvatarUrl: null,
        attachments: [],
        isFailed: true,
        error: mutation.error ?? 'Failed to send',
        mutationId: mutation.id,
      }
    })

    return [...failedComments, ...pendingComments, ...comments]
  }, [comments, outboxMutations])
  useEffect(() => subscribeChecklistProofs(item.id, setProofs), [item.id])

  const parent = item.parentContainerId ? elements.find((element) => element.id === item.parentContainerId) ?? null : null
  const focusedOccurrenceLabel = useMemo(
    () => occurrenceOptions.find((option) => option.key === effectiveSelectedOccurrenceKey)?.label ?? null,
    [occurrenceOptions, effectiveSelectedOccurrenceKey],
  )
  const breadcrumbSegments = useMemo(
    () => resolveDefaultBreadcrumb(item, elements, { focusedOccurrenceLabel }),
    [elements, focusedOccurrenceLabel, item],
  )
  const actor = useMemo(
    () => currentUser ? { id: currentUser.uid, uid: currentUser.uid, role: activeRole, name: displayName || currentUser.email || 'Member' } : null,
    [activeRole, currentUser, displayName],
  )
  const editLock = useEditLock(item?.id ?? null, {
    workspaceId: item.workspaceId,
    actor: actor ? { uid: actor.uid, displayName: actor.name ?? 'A user' } : null,
    isAdmin: Boolean(actor?.role && roleCanPerform(actor.role, 'ADMIN')),
    onLockConflict: (holder) => showToast(`${holder} is editing this item.`, 'info'),
  })
  const withLock = useCallback(
    <Args extends readonly unknown[], R>(fn: (...args: Args) => Promise<R> | R) => {
      return async (...args: Args): Promise<R | undefined> => {
        const ok = await editLock.acquireOnEdit()
        if (!ok) return undefined
        const result = await fn(...args)
        editLock.markActivity()
        return result
      }
    },
    [editLock],
  )
  const canEditStructure = canPerformOnNode('checklist.edit_structure', item.id)
  const canAssign = canPerformOnNode('checklist.assign', item.id)
  const canManageFieldPasses = canPerform('workspace.manage_field_passes')
  const canChangeStatus = canPerformOnNode('checklist.change_status', item.id)
  const canAttachProof = canPerformOnNode('checklist.manage_proof', item.id)
  const canComment = canPerformOnNode('checklist.comment', item.id)
  const itemById = useMemo(() => new Map(checklistItems.map((candidate) => [candidate.id, candidate])), [checklistItems])
  const dependencyState = useMemo(
    () => getChecklistDependencyBlockState(
      item,
      itemById,
      effectiveSelectedOccurrenceKey ?? (item.checklistType === 'WEEKLY' ? weekKey : todayIso),
    ),
    [effectiveSelectedOccurrenceKey, item, itemById, todayIso, weekKey],
  )
  const blockedTodoPolicy = normalizeChecklistBlockedTodoPolicy(activeWorkspace)
  const completionLocked = dependencyState.isBlocked && !blockedTodoPolicy.allowCompletion
  const proofLocked = dependencyState.isBlocked && !blockedTodoPolicy.allowProof
  const statusLocked = dependencyState.isBlocked && !blockedTodoPolicy.allowStatusChange
  const commentLocked = dependencyState.isBlocked && !blockedTodoPolicy.allowComment
  const editable = canEditStructure &&
    Boolean(actor) &&
    !(editLock.holder && !editLock.heldByMe)

  const hasManualSaveDraft = useMemo(() => {
    const isFieldDirty = (key: keyof typeof draft): boolean => {
      switch (key) {
        case 'title':
          return draft.title.trim() !== (item.title ?? '').trim()
        case 'description':
          return draft.description !== (item.description ?? '')
        case 'completionCriteria':
          return draft.completionCriteria !== (item.completionCriteria ?? '')
        case 'weightage':
          return (toOptionalNumber(draft.weightage) ?? 1) !== (item.weightage ?? 1)
        case 'priority':
          return (toOptionalNumber(draft.priority) ?? 1) !== (item.priority ?? 1)
        case 'complexityStars':
          return (toOptionalNumber(draft.complexityStars) ?? 3) !== (item.complexityStars ?? 3)
        case 'urgencyStars':
          return (toOptionalNumber(draft.urgencyStars) ?? 3) !== (item.urgencyStars ?? 3)
        case 'importanceStars':
          return (toOptionalNumber(draft.importanceStars) ?? 3) !== (item.importanceStars ?? 3)
        case 'estimatedHours':
          return toOptionalNumber(draft.estimatedHours) !== (item.estimatedHours ?? null)
        case 'estimatedCost':
          return toOptionalNumber(draft.estimatedCost) !== (item.estimatedCost ?? null)
        case 'actualCost':
          return toOptionalNumber(draft.actualCost) !== (item.actualCost ?? null)
        case 'currencyCode':
          return (draft.currencyCode.trim().toUpperCase() || 'USD') !== (item.currencyCode ?? 'USD')
        case 'scheduleStartDate':
          return draft.scheduleStartDate !== toInputDate(item.scheduleStartDate ?? item.startDate)
        case 'scheduleEndDate':
          return draft.scheduleEndDate !== toInputDate(item.scheduleEndDate ?? item.endDate)
        case 'scheduleTimeFrom':
          return draft.scheduleTimeFrom !== (item.scheduleTimeFrom ?? '')
        case 'scheduleTimeTo':
          return draft.scheduleTimeTo !== (item.scheduleTimeTo ?? '')
        case 'countdownHours':
          return toOptionalNumber(draft.countdownHours) !== (item.countdownHours ?? null)
        case 'delayDurationHours':
          return toOptionalNumber(draft.delayDurationHours) !== (item.delayDurationHours ?? null)
        default:
          return false
      }
    }

    return (Object.keys(draft) as Array<keyof typeof draft>).some(
      (fieldName) => isManualSaveField(fieldName) && isFieldDirty(fieldName),
    )
  }, [draft, item])

  useEffect(() => {
    editLock.armIdleRelease(!hasManualSaveDraft)
  }, [hasManualSaveDraft, editLock])

  const [showDiscardDialog, setShowDiscardDialog] = useState(false)

  const handleCloseRequest = useCallback(() => {
    if (editLock.holder && !editLock.heldByMe) {
      onClose()
      return
    }

    if (shouldPromptDiscard(hasManualSaveDraft, Boolean(editLock.holder && !editLock.heldByMe))) {
      setShowDiscardDialog(true)
      return
    }

    void editLock.release()
    onClose()
  }, [editLock, hasManualSaveDraft, onClose, setShowDiscardDialog])

  usePushBackHandler(handleCloseRequest, [handleCloseRequest])

  const [prevItem, setPrevItem] = useState(item)
  if (item !== prevItem) {
    setPrevItem(item)
    if (item.id !== prevItem.id || !hasManualSaveDraft) {
      setDraft(getInitialDraft(item))
    }
    setSelectedAssigneeId(item.assigneeId ?? '')
  }

  const effectiveKey = effectiveSelectedOccurrenceKey ?? (item.checklistType === 'WEEKLY' ? weekKey : todayIso)
  const itemId = item?.id
  const memberLockTargets = useMemo<LockTarget[]>(() => {
    if (!itemId) return []
    return [{ itemId, periodKey: effectiveKey }]
  }, [itemId, effectiveKey])

  const memberLockMap = useChecklistLockState(checklistItems, memberLockTargets, {
    workspaceId: item.workspaceId,
    timeZone,
  })

  const memberLock = useMemo(() => {
    const entry = memberLockMap.get(`${item.id}:${effectiveKey}`) ?? memberLockMap.get(`${item.id}|${effectiveKey}`)
    if (entry) return entry
    const graph = buildLockGraph(checklistItems)
    return resolveLock(item, effectiveKey, graph, memberLockMap.pages ?? occurrencePages, undefined, timeZone)
  }, [checklistItems, effectiveKey, item, memberLockMap, occurrencePages, timeZone])

  const incompleteDependencyIds = useMemo(() => {
    if (!memberLock.isLocked) return []
    return memberLock.reasons.map((r) => r.targetId).filter(Boolean) as string[]
  }, [memberLock])

  const evalTarget = useMemo(() => ({
    ...item,
    proofFileUrl: item.proofFileUrl || (proofs.length > 0 ? proofs[0].fileUrl : null),
    proofSubmissionId: item.proofSubmissionId || (proofs.length > 0 ? proofs[0].id : null),
    dependencyAlertNodeIds: incompleteDependencyIds,
  }), [incompleteDependencyIds, item, proofs])

  const [prevInitialPendingStatus, setPrevInitialPendingStatus] = useState(initialPendingStatus)
  const [pendingStatus, setPendingStatus] = useState<ChecklistWorkflowStatus | null>(() => {
    if (initialPendingStatus && initialPendingStatus in STATUS_META) {
      return initialPendingStatus
    }
    if (typeof window !== 'undefined') {
      const urlStatus = new URLSearchParams(window.location.search).get('pendingStatus')
      if (urlStatus && urlStatus in STATUS_META) {
        return urlStatus as ChecklistWorkflowStatus
      }
    }
    return null
  })
  const [serverBlockers, setServerBlockers] = useState<BlockerEntry[]>([])
  const isCommittingPendingRef = useRef(false)

  if (initialPendingStatus !== prevInitialPendingStatus) {
    setPrevInitialPendingStatus(initialPendingStatus)
    if (initialPendingStatus && initialPendingStatus in STATUS_META) {
      setPendingStatus(initialPendingStatus)
      setServerBlockers([])
    }
  }

  const pendingBlockers = useMemo<BlockerEntry[]>(() => {
    if (!pendingStatus) return []
    const clientEval = evaluateBlockers(evalTarget, actor, pendingStatus)
    if (!clientEval.ok) {
      return clientEval.blockers
    }
    if (serverBlockers.length > 0) {
      return serverBlockers
    }
    return []
  }, [actor, evalTarget, pendingStatus, serverBlockers])

  useEffect(() => {
    if (!pendingStatus || pendingBlockers.length > 0) return
    if (isCommittingPendingRef.current) return
    if (!canChangeStatus || !actor) return

    let isMounted = true
    isCommittingPendingRef.current = true

    void (async () => {
      try {
        if (!editLock.heldByMe) {
          const acquired = await editLock.acquireOnEdit()
          if (!acquired) return
        }
        editLock.markActivity()
        const currentItemWorkflowStatus = item.workflowStatus ?? (item.isCompleted ? 'COMPLETED' : 'CREATED')
        await updateChecklistWorkflowStatus({
          item: {
            id: item.id,
            boardStatus: item.boardStatus,
            recordVersion: item.recordVersion,
            workflowStatus: currentItemWorkflowStatus,
            workspaceId: item.workspaceId,
            checklistType: item.checklistType,
          },
          workflowStatus: pendingStatus,
          actor: { id: actor.uid, name: actor.name ?? '' },
          periodKey: recurringMode ? effectiveSelectedOccurrenceKey : null,
          periodBoardStatus: recurringMode && effectiveSelectedOccurrenceKey ? item.occurrences?.[effectiveSelectedOccurrenceKey]?.boardStatus : undefined,
          pageKey: recurringMode ? effectiveSelectedOccurrenceKey : null,
          runDelta: applyStripboardDelta,
        })
        if (isMounted) {
          setPendingStatus(null)
          setServerBlockers([])
          showToast('Checklist status updated.', 'success')
        }
      } catch (error) {
        if (isMounted) {
          if (error instanceof ChecklistBlockerError) {
            setServerBlockers(
              error.blockers.map((b) => ({
                code: b.code,
                message: b.message,
                ctaSection: ctaSectionFor(b.code),
              })),
            )
          } else {
            showToast(error instanceof Error ? error.message : 'Could not update status.', 'error')
          }
        }
      } finally {
        isCommittingPendingRef.current = false
      }
    })()

    return () => {
      isMounted = false
    }
  }, [actor, canChangeStatus, editLock, effectiveSelectedOccurrenceKey, item, pendingBlockers.length, pendingStatus, recurringMode, showToast])
  const assigneeOptions = useMemo(() => {
    if (!item.assigneeId || workspaceMembers.some((member) => member.userId === item.assigneeId)) return workspaceMembers
    return [
      ...workspaceMembers,
      {
        userId: item.assigneeId,
        displayName: item.assigneeName || 'Current assignee',
        email: '',
        role: 'VIEWER' as const,
      },
    ]
  }, [item.assigneeId, item.assigneeName, workspaceMembers])
  const wiredDependencies = useMemo(
    () => (item.dependencyNodeIds ?? [])
      .map((dependencyId) => itemById.get(dependencyId))
      .filter((candidate): candidate is ChecklistItem => Boolean(candidate)),
    [item.dependencyNodeIds, itemById],
  )

  const updateDraft = withLock((key: keyof typeof draft, value: string) => {
    setDraft((current) => ({ ...current, [key]: value }))
  })

  const saveDetails = async (): Promise<boolean> => {
    if (!actor || !editable) return false
    if (!editLock.heldByMe) {
      const acquired = await editLock.acquireOnEdit()
      if (!acquired) return false
    }
    editLock.markActivity()
    setBusy(true)
    try {
      await updateChecklistFields(firestoreDb, {
        item,
        actor,
        pageKey: recurringMode ? effectiveSelectedOccurrenceKey : null,
        updates: {
          title: draft.title.trim() || item.title,
          description: draft.description,
          completionCriteria: draft.completionCriteria,
          weightage: Math.max(1, Math.round(toOptionalNumber(draft.weightage) ?? 1)),
          priority: Math.max(1, Math.min(5, Math.round(toOptionalNumber(draft.priority) ?? 1))),
          complexityStars: Math.max(1, Math.min(5, Math.round(toOptionalNumber(draft.complexityStars) ?? 3))),
          urgencyStars: Math.max(1, Math.min(5, Math.round(toOptionalNumber(draft.urgencyStars) ?? 3))),
          importanceStars: Math.max(1, Math.min(5, Math.round(toOptionalNumber(draft.importanceStars) ?? 3))),
          estimatedHours: toOptionalNumber(draft.estimatedHours),
          estimatedCost: toOptionalNumber(draft.estimatedCost),
          actualCost: toOptionalNumber(draft.actualCost),
          currencyCode: draft.currencyCode.trim().toUpperCase() || 'USD',
          scheduleStartDate: draft.scheduleStartDate || null,
          scheduleEndDate: draft.scheduleEndDate || null,
          startDate: draft.scheduleStartDate || null,
          endDate: draft.scheduleEndDate || null,
          hasTimeWindow: Boolean(draft.scheduleTimeFrom || draft.scheduleTimeTo),
          scheduleTimeFrom: draft.scheduleTimeFrom || null,
          scheduleTimeTo: draft.scheduleTimeTo || null,
          countdownHours: toOptionalNumber(draft.countdownHours),
          delayDurationHours: toOptionalNumber(draft.delayDurationHours),
          attachedFormIds: attachedProvisionIds.forms,
          attachedDesignIds: attachedProvisionIds.designs,
          attachedUploadIds: attachedProvisionIds.uploads,
          attachedLinkIds: attachedProvisionIds.links,
        },
        summary: `Updated checklist item ${draft.title.trim() || item.title}`,
      })
      showToast('Checklist details saved.', 'success')
      await editLock.release()
      return true
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not save checklist details.', 'error')
      return false
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    if (!hasManualSaveDraft) return
    return registerSwitchGuard({
      id: 'member-checklist-edit',
      describe: () => (hasManualSaveDraft ? t('NLSMemberChecklistEditGuardDescription', { title: item.title }) : null),
      save: async () => {
        await saveDetails()
      },
      discard: () => {
        setDraft(getInitialDraft(item))
      },
    })
  }, [hasManualSaveDraft, item, saveDetails, t])

  const commitAssignment = async (
    assignee: ChecklistMemberOption | null,
    scope: 'single' | 'future' | 'global',
  ) => {
    if (!actor || !canAssign) return
    if (!editLock.heldByMe) {
      const acquired = await editLock.acquireOnEdit()
      if (!acquired) return
    }
    editLock.markActivity()
    setBusy(true)
    try {
      const resolvedItem = {
        ...item,
        workflowStatus: item.workflowStatus ?? (item.isCompleted ? 'COMPLETED' : 'CREATED'),
      }
      await assignChecklistItem(firestoreDb, {
        item: resolvedItem,
        assignee,
        actor: { id: actor.uid, name: actor.name ?? '' },
        pageKey: recurringMode ? effectiveSelectedOccurrenceKey : null,
        scope,
      })
      const label = assignee ? `Assigned to ${assignee.displayName}` : 'Assignment removed'
      const suffix = scope === 'single'
        ? ' for this occurrence.'
        : scope === 'future'
          ? ' for this and future occurrences.'
          : '.'
      showToast(`${label}${suffix}`, 'success')
    } catch (error) {
      setSelectedAssigneeId(item.assigneeId ?? '')
      showToast(error instanceof Error ? error.message : 'Could not update assignment.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const changeAssignment = async (assigneeId: string) => {
    if (!actor || !canAssign) return
    const assignee = assigneeOptions.find((member) => member.userId === assigneeId) ?? null
    setSelectedAssigneeId(assignee?.userId ?? '')
    // Recurring + a focused occurrence → prompt for scope. Otherwise the
    // definition-scoped write is the only sensible choice.
    if (recurringMode && effectiveSelectedOccurrenceKey) {
      setPendingAssignee({ assignee })
      return
    }
    await commitAssignment(assignee, 'global')
  }

  const changeStatus = async (
    status: ChecklistWorkflowStatus,
    opts?: { notRequiredScope?: 'FROM_PERIOD' | 'CLEAR' | null; skipScopePrompt?: boolean },
  ) => {
    if (!canChangeStatus || !actor) return
    if (statusLocked || (status === 'COMPLETED' && completionLocked)) {
      const blockerTitle = dependencyState.reasons.find((r) => r.title)?.title
      const message = blockerTitle
        ? `Blocked: ${blockerTitle}`
        : (status === 'COMPLETED' ? 'Blocked by dependency. Completion is disabled.' : 'Blocked by dependency. Status changes are disabled.')
      showToast(message, 'error')
      return
    }

    if (!editLock.heldByMe) {
      const acquired = await editLock.acquireOnEdit()
      if (!acquired) return
    }
    editLock.markActivity()

    const clientEval = evaluateBlockers(evalTarget, actor, status)
    if (!clientEval.ok) {
      setPendingStatus(status)
      setServerBlockers([])
      return
    }

    if (
      status === 'NOT_REQUIRED'
      && recurringMode
      && !opts?.skipScopePrompt
    ) {
      setNotNeededScopeChoice('THIS_PERIOD')
      setNotNeededScopeDialogOpen(true)
      return
    }

    const effectiveNotRequiredScope =
      opts?.notRequiredScope !== undefined
        ? opts.notRequiredScope
        : status !== 'NOT_REQUIRED' && item.notRequiredFrom
          ? 'CLEAR'
          : null

    setBusy(true)
    try {
      const currentItemWorkflowStatus = item.workflowStatus ?? (item.isCompleted ? 'COMPLETED' : 'CREATED')
      await updateChecklistWorkflowStatus({
        item: {
          id: item.id,
          boardStatus: item.boardStatus,
          recordVersion: item.recordVersion,
          workflowStatus: currentItemWorkflowStatus,
          workspaceId: item.workspaceId,
          checklistType: item.checklistType,
        },
        workflowStatus: status,
        actor: { id: actor.uid, name: actor.name ?? '' },
        periodKey: recurringMode ? effectiveSelectedOccurrenceKey : null,
        periodBoardStatus: recurringMode && effectiveSelectedOccurrenceKey ? item.occurrences?.[effectiveSelectedOccurrenceKey]?.boardStatus : undefined,
        pageKey: recurringMode ? effectiveSelectedOccurrenceKey : null,
        notRequiredScope: effectiveNotRequiredScope,
        runDelta: applyStripboardDelta,
      })
      setPendingStatus(null)
      setServerBlockers([])
      showToast('Checklist status updated.', 'success')
    } catch (error) {
      if (error instanceof ChecklistBlockerError) {
        setPendingStatus(status)
        setServerBlockers(
          error.blockers.map((b) => ({
            code: b.code,
            message: b.message,
            ctaSection: ctaSectionFor(b.code),
          })),
        )
        return
      }
      const message = error instanceof Error ? error.message : ''
      if (message.toLowerCase().includes('version') || message.toLowerCase().includes('conflict')) {
        showToast('Status conflict: the checklist was updated elsewhere. Please refresh.', 'warning')
      } else {
        showToast(error instanceof Error ? error.message : 'Could not update checklist status.', 'error')
      }
    } finally {
      setBusy(false)
    }
  }

  const isUnassigned = !item.assigneeId
  const commentComposerDisabled = !canComment || commentLocked || isUnassigned

  const submitComment = async () => {
    if (!actor || commentComposerDisabled || !commentDraft.trim()) return
    if (!editLock.heldByMe) {
      const acquired = await editLock.acquireOnEdit()
      if (!acquired) return
    }
    editLock.markActivity()
    const body = commentDraft
    try {
      await submitHelperComment({ item, actor, body, actorRole: 'MEMBER', waitForPersist: true })
      clearCommentDraft()
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not add comment.', 'error')
    }
  }

  const handleRetryComment = async (mutationId: string) => {
    try {
      await retryMutation(mutationId)
      showToast('Retrying comment…', 'info')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not retry comment.', 'error')
    }
  }

  const uploadProof = async (file: File) => {
    if (!actor || !canAttachProof || proofLocked) return
    if (!editLock.heldByMe) {
      const acquired = await editLock.acquireOnEdit()
      if (!acquired) return
    }
    editLock.markActivity()
    try {
      await submitHelperProof({ item, actor, file, actorRole: 'MEMBER' })
      showToast('Proof queued for sync.', 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not attach proof.', 'error')
    }
  }

  const currentStatus = item.workflowStatus ?? (item.isCompleted ? 'COMPLETED' : 'CREATED')
  type HeroChip = { key: string; label: string; tone: 'schedule' }
  const heroChips: HeroChip[] = [
    {
      key: 'recurring',
      label: recurringMode ? `Recurring · ${recurringMode === 'DAILY' ? 'Daily' : 'Weekly'}` : 'Individual checklist',
      tone: 'schedule',
    },
  ]
  const stepWeight = withLock((delta: number) => {
    setDraft((current) => {
      const next = Math.max(1, Math.round((toOptionalNumber(current.weightage) ?? 1) + delta))
      return { ...current, weightage: String(next) }
    })
  })
  const parentPath = breadcrumbSegments.map((segment) => segment.label).join(' · ') || parent?.title || 'Workspace checklist'
  const openAttachmentPicker = (field: 'description' | 'completionCriteria' | 'comment') => {
    if (pickerState) return
    setPickerState({ field })
  }
  const selectWorkspaceItem = (reference: WorkspaceItemReference) => {
    if (!pickerState) return
    const provisionKey = reference.kind === 'form' ? 'forms'
      : reference.kind === 'design' ? 'designs'
        : reference.kind === 'upload' ? 'uploads'
          : reference.kind === 'link' ? 'links' : null
    if (!provisionKey) return
    setAttachedProvisionIds((current) => ({
      ...current,
      [provisionKey]: Array.from(new Set([...current[provisionKey], reference.id])),
    }))
    const { field } = pickerState
    setFieldAttachments((current) => ({
      ...current,
      [field]: current[field].some((entry) => entry.id === reference.id && entry.kind === reference.kind)
        ? current[field]
        : [...current[field], reference],
    }))
    if (field === 'comment') {
      setCommentDraft((current) => `${current}${current ? ' ' : ''}[${reference.label}]`)
    }
    setPickerState(null)
  }
  const commitWorkspaceItems = (references: WorkspaceItemReference[]) => {
    if (!pickerState) return
    const field = pickerState.field
    setAttachedProvisionIds((current) => references.reduce((next, reference) => {
      const provisionKey = reference.kind === 'form' ? 'forms'
        : reference.kind === 'design' ? 'designs'
          : reference.kind === 'upload' ? 'uploads'
            : reference.kind === 'link' ? 'links' : null
      return provisionKey ? { ...next, [provisionKey]: Array.from(new Set([...next[provisionKey], reference.id])) } : next
    }, current))
    setFieldAttachments((current) => ({
      ...current,
      [field]: Array.from(new Map([...current[field], ...references].map((reference) => [`${reference.kind}:${reference.id}`, reference])).values()),
    }))
    if (field === 'comment') {
      const mentions = references.map((r) => `[${r.label}]`).join(' ')
      setCommentDraft((current) => `${current}${current ? ' ' : ''}${mentions}`)
    }
    setPickerState(null)
  }
  const attachedItems: WorkspaceItemReference[] = Array.from(new Map<string, WorkspaceItemReference>([
    ...wiredDependencies.map((candidate): [string, WorkspaceItemReference] => [`checklist:${candidate.id}`, { id: candidate.id, label: candidate.title, kind: 'checklist' as const, parentId: candidate.parentContainerId ?? null, ancestors: [] }]),
    ...Object.values(fieldAttachments).flat().map((reference): [string, WorkspaceItemReference] => [`${reference.kind}:${reference.id}`, reference]),
  ]).values())

  return (
    <ItemTimeProvider workspaceId={activeWorkspaceId}>
      {/* eslint-disable-next-line no-restricted-syntax -- member full overlay checklist page shell with gesture dismiss is not a Common modal */}
      <div className="memberFullOverlay memberChecklistDetailPage chkPanel chkPanel--mobile chkPanel--adopted" role="dialog" aria-modal="true" aria-label="Checklist details">
      <ChecklistDetailHero
        breadcrumb={parentPath}
        status={STATUS_LABEL[currentStatus]}
        onBack={handleCloseRequest}
        onSubtitleClick={onOpenWorkspaceItem ? () => setCrumbSheetOpen(true) : undefined}
        trailing={(
          <div className="flex items-center gap-1.5">
            {memberLock.isLocked && (
              <DependencyLockChip isLocked={memberLock.isLocked} reasons={memberLock.reasons} />
            )}
            {memberLock.reasons.some((r) => r.missedPeriods && r.missedPeriods.length > 0) && (
              <MissedPeriodsChip
                missedPeriods={memberLock.reasons.find((r) => r.missedPeriods && r.missedPeriods.length > 0)?.missedPeriods}
              />
            )}
            {!canEditStructure ? (
              <span className="memberDetailLock"><LockRoundedIcon fontSize="small" /> Read only</span>
            ) : editLock.holder && !editLock.heldByMe ? (
              <Button variant="tertiary" size="md" className="memberDetailRecheck" onClick={() => void editLock.recheck()} disabled={editLock.isCheckingLock} title={`Locked by ${editLock.holder}. Tap to re-check.`}>
                <RefreshRoundedIcon fontSize="small" className={editLock.isCheckingLock ? 'isChecking' : undefined} /> Re-check · {editLock.holder}
              </Button>
            ) : null}
          </div>
        )}
      />
      <nav
        className="memberChecklistDetailTabs"
        role="tablist"
        aria-label="Checklist detail sections"
        style={{ '--tab-count': DETAIL_TABS.length } as CSSProperties}
      >
        {DETAIL_TABS.map((tab, index) => {
          const selected = activeDetailTab === tab.key
          const Icon = selected ? tab.iconActive : tab.icon
          return (
            <Pressable
              id={`tab-${tab.key}`}
              key={tab.key}
              ref={(element) => {
                tabRefs.current[index] = element
              }}
              role="tab"
              aria-selected={selected}
              aria-controls={`tab-panel-${tab.key}`}
              tabIndex={selected ? 0 : -1}
              className={`memberChecklistDetailTab ${selected ? 'isActive' : ''}`}
              onClick={() => selectTab(tab.key)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
            >
              <span className="memberChecklistDetailTabIcon" aria-hidden="true">
                <Icon fontSize="inherit" />
              </span>
              <span className="memberChecklistDetailTabLabel">{tab.label}</span>
            </Pressable>
          )
        })}
      </nav>
      <main className="memberFullOverlayBody memberChecklistDetailGrid">
        <StatusBlockerBanner
          pendingStatus={pendingStatus}
          blockers={pendingBlockers}
          onDismiss={() => {
            setPendingStatus(null)
            setServerBlockers([])
          }}
          onBlockerClick={(section) => jumpToSection(section as BlockerCtaSection)}
        />
        <section
          id="tab-panel-overview"
          role="tabpanel"
          tabIndex={0}
          hidden={activeDetailTab !== 'overview'}
          aria-labelledby="tab-overview"
          className="memberChecklistDetailTabBody"
        >
        <section className="memberDetailCard memberChecklistHeroCard" aria-label="Checklist overview">
          {recurringMode && occurrenceOptions.length > 0 ? (
            <div className="memberChecklistOccurrenceWrapper">
              <label className="memberChecklistOccurrenceField">
                <span><RepeatRoundedIcon fontSize="inherit" /> Occurrence</span>
                <Select
                  trigger="pill"
                  ariaLabel="Recurring checklist occurrence"
                  value={effectiveSelectedOccurrenceKey}
                  className="memberChecklistOccurrenceSelect"
                  searchable={occurrenceOptions.length >= 8}
                  options={occurrenceOptions.map((option) => ({
                    value: option.key,
                    label: (
                      <span className="memberChecklistOccurrenceOption">
                        <strong>{option.label}</strong>
                        <small>{option.meta}</small>
                      </span>
                    ),
                    searchText: `${option.label} ${option.meta}`,
                  }))}
                  onChange={setSelectedOccurrenceKey}
                />
              </label>
            </div>
          ) : null}
          <label id="mobile-checklist-section-title">
            Name
            <TextInput
              block
              value={draft.title}
              disabled={!editable}
              placeholder="Checklist name…"
              onChange={(val) => updateDraft('title', val)}
            />
          </label>
          <label id="mobile-checklist-section-description">
            Description
            <Textarea
              block
              ref={descriptionRef}
              value={draft.description}
              disabled={!editable}
              placeholder="Describe the work this checklist tracks…"
              onChange={(val) => {
                updateDraft('description', val)
                onDescriptionMention(val)
              }}
            />
          </label>
          <label id="mobile-checklist-section-completionCriteria">
            Completion criteria
            <Textarea
              block
              ref={completionCriteriaRef}
              value={draft.completionCriteria}
              disabled={!editable}
              placeholder={'Define what "done" looks like…'}
              onChange={(val) => {
                updateDraft('completionCriteria', val)
                onCompletionCriteriaMention(val)
              }}
            />
          </label>
          <IconButton
            variant="tertiary"
            size="md"
            className="memberAttachButton"
            label="Attach workspace item"
            icon={<AddRoundedIcon fontSize="small" />}
            disabled={Boolean(pickerState) || !editable}
            onClick={() => openAttachmentPicker('description')}
          />
          <div className="memberChecklistHeroChipRow">
            {heroChips.map((chip) => (
              <span
                key={chip.key}
                className="memberChecklistHeroChip"
                data-tone={chip.tone}
              >
                {chip.label}
              </span>
            ))}
          </div>
        </section>
        <section className="memberDetailCard memberAttachedItems" aria-label="Attached items">
          <header><h3>Attached items</h3></header>
          {attachedItems.length === 0 ? <EmptyHubState compact appearance="card" title="No attached items." description="Attach workspace context from a text field." /> : attachedItems.map((reference) => {
            const interactive = Boolean(onOpenWorkspaceItem)
            return <div className="memberAttachedItemRow" key={`${reference.kind}:${reference.id}`}>
              <Pressable disabled={!interactive} onClick={() => onOpenWorkspaceItem?.(reference)}>
                <span>{reference.label}</span><small>{reference.kind} · {parentPath}</small>{interactive ? <ChevronRightRoundedIcon fontSize="small" aria-hidden /> : null}
              </Pressable>
              <IconButton variant="tertiary" tone="danger" size="md" label={`Remove ${reference.label}`} icon={<CloseRoundedIcon fontSize="small" />} onClick={() => {
                const provisionKey = reference.kind === 'form' ? 'forms' : reference.kind === 'design' ? 'designs' : reference.kind === 'upload' ? 'uploads' : reference.kind === 'link' ? 'links' : null
                if (provisionKey) setAttachedProvisionIds((current) => ({ ...current, [provisionKey]: current[provisionKey].filter((id) => id !== reference.id) }))
                setFieldAttachments((current) => Object.fromEntries(Object.entries(current).map(([field, entries]) => [field, entries.filter((entry) => entry.id !== reference.id || entry.kind !== reference.kind)])) as typeof current)
              }} />
            </div>
          })}
        </section>
        </section>

        <section
          id="tab-panel-details"
          role="tabpanel"
          tabIndex={0}
          hidden={activeDetailTab !== 'details'}
          aria-labelledby="tab-details"
          className="memberChecklistDetailTabBody"
        >
        <section className="memberDetailCard">
          <header>
            <h3>Core details</h3>
            {/* 4c — Save is always visible so members know edits can be saved.
             * Disabled until there is a manual-save draft, the lock is held by
             * the current user (editable = false when holder && !heldByMe), or
             * a write is in flight. Title surfaces the lock-holder name when
             * another user owns the lock. */}
            <IconButton
              size="sm"
              variant="primary"
              icon={<SaveRoundedIcon fontSize="small" />}
              label="Save"
              disabled={!hasManualSaveDraft || !editable || busy}
              title={
                editLock.holder && !editLock.heldByMe
                  ? `${editLock.holder} is editing this item`
                  : undefined
              }
              onClick={() => void saveDetails()}
            />
          </header>
          <div className="memberDetailFieldGrid memberDetailFieldGrid--compact">
            <label>
              Weight
              <span className="memberStepperInput">
                <Pressable disabled={!editable} onClick={() => stepWeight(-1)} aria-label="Decrease weight">&lt;</Pressable>
                <TextInput inputMode="numeric" value={draft.weightage} disabled={!editable} onChange={(val) => updateDraft('weightage', val)} />
                <Pressable disabled={!editable} onClick={() => stepWeight(1)} aria-label="Increase weight">&gt;</Pressable>
              </span>
            </label>
            <label>
              Status
              <SheetSelect<ChecklistWorkflowStatus>
                ariaLabel="Checklist status"
                placeholder="Select status"
                value={currentStatus}
                triggerClassName={currentStatus ? `memberStatusSelect ${STATUS_TONE_CLASS[currentStatus]}` : undefined}
                disabled={!canChangeStatus || busy || statusLocked}
                options={filterWorkflowStatusOptions(item, actor)
                  .filter((option) => CHECKLIST_STATUSES.includes(option.value))
                  .map((option) => ({
                    value: option.value,
                    label: STATUS_LABEL[option.value],
                    disabled: !canPickInDropdown(evalTarget, actor, option.value).ok,
                    className: STATUS_TONE_CLASS[option.value],
                  }))}
                onChange={(value) => void changeStatus(value)}
              />
            </label>
          </div>
          <section className="memberSignalsCard" id="mobile-checklist-section-signals" aria-label="Signals">
            <p className="memberDetailGroupHeading">Signals</p>
          <div className="memberRatingGrid">
            <RatingInput label="Priority" value={draft.priority} disabled={!editable} onChange={(value) => updateDraft('priority', value)} />
            <RatingInput label="Complexity" value={draft.complexityStars} disabled={!editable} onChange={(value) => updateDraft('complexityStars', value)} />
            <RatingInput label="Urgency" value={draft.urgencyStars} disabled={!editable} onChange={(value) => updateDraft('urgencyStars', value)} />
            <RatingInput label="Importance" value={draft.importanceStars} disabled={!editable} onChange={(value) => updateDraft('importanceStars', value)} />
          </div>
          </section>
        </section>
        <section className="memberDetailCard" id="mobile-checklist-section-resources">
          <header><h3>Schedule and budget</h3></header>
          <div className="memberDetailFieldGroup" id="mobile-checklist-section-dates">
            <p className="memberDetailGroupHeading">Window</p>
            <div className="memberDetailFieldGrid">
              <label>
                Start date
                <DatePicker
                  ariaLabel="Start date"
                  value={draft.scheduleStartDate}
                  placeholder="Select date"
                  disabled={!editable}
                  onChange={(value) => updateDraft('scheduleStartDate', value)}
                />
              </label>
              <label>
                End date
                <DatePicker
                  ariaLabel="End date"
                  value={draft.scheduleEndDate}
                  placeholder="Select date"
                  disabled={!editable}
                  onChange={(value) => updateDraft('scheduleEndDate', value)}
                />
              </label>
            </div>
          </div>
          <div className="memberDetailFieldGroup">
            <p className="memberDetailGroupHeading">Effort and budget</p>
            <div className="memberDetailFieldGrid">
              <label>
                Estimated hours
                <span className="memberNumericAffixWrap">
                  <TextInput inputMode="decimal" aria-label="Estimated hours" value={draft.estimatedHours} disabled={!editable} onChange={(val) => updateDraft('estimatedHours', val)} />
                  <span className="memberNumericAffix" aria-hidden>hrs</span>
                </span>
                <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <ItemTimerButton
                    workspaceId={item.workspaceId}
                    nodeId={item.id}
                    nodeLabel={item.title}
                    size="sm"
                  />
                  <ItemTimeDisplay
                    workspaceId={item.workspaceId}
                    nodeId={item.id}
                    estimatedHours={toOptionalNumber(draft.estimatedHours) ?? item.estimatedHours}
                    actualMinutes={item.actualMinutes}
                  />
                </div>
              </label>
              <label>
                Estimated cost
                <span className="memberNumericAffixWrap">
                  <span className="memberNumericAffix memberNumericAffix--prefix" aria-hidden>{getCurrencyAffix(draft.currencyCode)}</span>
                  <TextInput inputMode="decimal" aria-label="Estimated cost" value={draft.estimatedCost} disabled={!editable} onChange={(val) => updateDraft('estimatedCost', val)} />
                </span>
              </label>
              <label>
                Actual cost
                <span className="memberNumericAffixWrap">
                  <span className="memberNumericAffix memberNumericAffix--prefix" aria-hidden>{getCurrencyAffix(draft.currencyCode)}</span>
                  <TextInput inputMode="decimal" aria-label="Actual cost" value={draft.actualCost} disabled={!editable} onChange={(val) => updateDraft('actualCost', val)} />
                </span>
              </label>
              {/* TODO: hoist currency choices to config when a catalog exists. */}
              <label>Currency<SheetSelect ariaLabel="Currency" value={draft.currencyCode} placeholder="Select currency" disabled={!editable} options={['USD', 'EUR', 'GBP', 'INR'].map((value) => ({ value, label: value }))} onChange={(value) => updateDraft('currencyCode', value)} /></label>
            </div>
            {item.bindings?.budgetBinding && (
              <div className="memberBudgetBindingIndicator">
                <span className="memberNumericAffix">
                  {t('NLSMemberChecklistAutoSpendBadge')}
                </span>
                <span>
                  {item.sideEffects?.budgetSpends && item.sideEffects.budgetSpends.length > 0
                    ? t('NLSMemberChecklistSpendRecordedNotice', {
                        amount: `${getCurrencyAffix(activeWorkspace?.settings?.currency ?? 'USD')} ${(item.sideEffects.budgetSpends[item.sideEffects.budgetSpends.length - 1].amountMinor / 100).toFixed(2)}`,
                      })
                    : t('NLSMemberChecklistAutoSpendNotice', {
                        category: item.bindings.budgetBinding.category,
                      })}
                </span>
              </div>
            )}
          </div>
        </section>
        </section>

        <section
          id="tab-panel-assignment"
          role="tabpanel"
          tabIndex={0}
          hidden={activeDetailTab !== 'assignment'}
          aria-labelledby="tab-assignment"
          className="memberChecklistDetailTabBody"
        >
        <section className="memberDetailCard" id="mobile-checklist-section-assignee">
          <header><h3>Assignment and dependencies</h3></header>
          <label>
            Assignee
            <SheetSelect
              ariaLabel="Checklist assignee"
              placeholder="Select assignee"
              value={selectedAssigneeId}
              disabled={!canAssign || busy}
              options={[{ value: '', label: 'Unassigned' }, ...assigneeOptions.map((member) => ({ value: member.userId, label: `${member.displayName} (${member.role.toLocaleLowerCase()})` }))]}
              onChange={(value) => void changeAssignment(value)}
            />
          </label>
          <div className="memberDependencyList" id="mobile-checklist-section-dependencies">
            {wiredDependencies.length === 0 ? <p className="memberDetailEmpty">No dependencies. Nothing needs to happen before this can start.</p> : wiredDependencies.map((candidate) => {
              const reference = { id: candidate.id, label: candidate.title, kind: 'checklist' as const, parentId: candidate.parentContainerId ?? null, ancestors: [] }
              const interactive = Boolean(onOpenWorkspaceItem)
              return (
                <Pressable key={candidate.id} className="memberDependencyRow" disabled={!interactive} onClick={() => onOpenWorkspaceItem?.(reference)}>
                  <span>{candidate.title}</span>
                  <small>{STATUS_LABEL[candidate.workflowStatus ?? (candidate.isCompleted ? 'COMPLETED' : 'CREATED')]}</small>
                  {interactive ? <ChevronRightRoundedIcon fontSize="small" aria-hidden /> : null}
                </Pressable>
              )
            })}
          </div>
        </section>

        {canManageFieldPasses ? (
          <section className="memberDetailCard" id="mobile-checklist-section-fieldPass">
            <header><h3>Field Pass</h3></header>
            <ChecklistFieldPassControl
              workspaceId={item.workspaceId}
              checklistItemId={item.id}
              checklistTitle={item.title}
              canManage
              compact
              deferInitialLoad={!visitedTabs.has('assignment')}
              onNotice={fieldPassOnNotice}
            />
          </section>
        ) : null}

        <div id="mobile-checklist-section-attachedForms">
          <ChecklistAttachedFormsSection
            formIds={item.attachedFormIds}
            itemTitle={item.title}
            tone="member"
            deferInitialLoad={!visitedTabs.has('assignment')}
          />
        </div>
        <ChecklistAttachedDesignsSection
          workspaceId={item.workspaceId}
          designIds={item.attachedDesignIds}
          itemTitle={item.title}
          tone="member"
        />
        </section>

        <section
          id="tab-panel-proof"
          role="tabpanel"
          tabIndex={0}
          hidden={activeDetailTab !== 'proof'}
          aria-labelledby="tab-proof"
          className="memberChecklistDetailTabBody"
        >
        <section className="memberDetailCard" id="mobile-checklist-section-proof">
          <header><h3>Proof and comments</h3></header>
          <div className="memberDetailFieldGroup">
            <p className="memberDetailGroupHeading">Proof</p>
            <HiddenFileInput
              ref={proofFileInputRef}
              disabled={!canAttachProof || proofLocked}
              onFiles={(files) => {
                const file = files[0]
                if (file) void uploadProof(file)
              }}
            />
            <Pressable
              className={`memberProofDrop ${!canAttachProof || proofLocked ? 'isDisabled' : ''}`}
              disabled={!canAttachProof || proofLocked}
              onClick={() => proofFileInputRef.current?.click()}
            >
              <AttachFileRoundedIcon />
              <span>{proofLocked ? 'Proof locked by dependency' : canAttachProof ? 'Attach proof file' : 'Proof upload restricted'}</span>
            </Pressable>
            <div className="memberEvidenceList">
              {proofs.length ? proofs.map((proof) => (
                <a key={proof.id} href={proof.fileUrl || undefined} target="_blank" rel="noreferrer">
                  <AttachFileRoundedIcon fontSize="small" />
                  <span>{proof.fileName ?? 'Proof attached'}</span>
                </a>
              )) : <EmptyHubState compact appearance="card" title="No uploaded proof yet." />}
            </div>
          </div>
          <div className="memberDetailFieldGroup">
            <p className="memberDetailGroupHeading">Comments</p>
            <div className="memberCommentComposer">
              <Textarea
                ref={commentRef}
                value={commentDraft}
                disabled={commentComposerDisabled}
                placeholder={
                  commentLocked
                    ? 'Comments locked by dependency'
                    : isUnassigned
                      ? 'Assign this item before commenting'
                      : canComment
                        ? 'Write a comment…'
                        : 'Comments restricted'
                }
                onChange={(val) => {
                  setCommentDraft(val)
                  onCommentMention(val)
                }}
              />
              <IconButton
                variant="tertiary"
                size="md"
                className="memberAttachButton"
                label="Attach to comment"
                icon={<AddRoundedIcon fontSize="small" />}
                disabled={Boolean(pickerState) || !editable || commentComposerDisabled}
                onClick={() => openAttachmentPicker('comment')}
              />
              <Button
                variant="primary"
                size="md"
                disabled={commentComposerDisabled || !commentDraft.trim()}
                onClick={() => void submitComment()}
              >
                Post
              </Button>
            </div>
            <div className="memberCommentList">
              {commentsError ? (
                <div className="memberCommentsErrorState" role="alert">
                  <ErrorOutlineRoundedIcon fontSize="small" />
                  <span>{commentsError}</span>
                </div>
              ) : null}
              {displayComments.length ? (
                displayComments.map((comment) => (
                  <article
                    key={comment.id}
                    className={`memberCommentCard ${comment.isPending ? 'isPending' : ''} ${comment.isFailed ? 'isFailed' : ''}`}
                  >
                    <div className="memberCommentHeader">
                      <strong>{comment.actorName}</strong>
                      <div className="memberCommentHeaderMeta">
                        {comment.isPending ? (
                          <StatusChip size="sm" tone="warning" className="memberCommentBadge isPending">Sending…</StatusChip>
                        ) : comment.isFailed ? (
                          <StatusChip size="sm" tone="danger" className="memberCommentBadge isFailed">Failed</StatusChip>
                        ) : (
                          <time>{formatCommentRelativeTime(comment.createdAt)}</time>
                        )}
                      </div>
                    </div>
                    <p>{comment.body}</p>
                    {comment.isFailed && comment.mutationId ? (
                      <div className="memberCommentFooter">
                        <span className="memberCommentErrorText">{comment.error || 'Failed to sync'}</span>
                        <Button
                          variant="tertiary"
                          size="md"
                          onClick={() => void handleRetryComment(comment.mutationId!)}
                        >
                          <RefreshRoundedIcon fontSize="inherit" />
                          Retry
                        </Button>
                      </div>
                    ) : null}
                  </article>
                ))
              ) : !commentsError ? (
                <EmptyHubState compact appearance="card" title="No comments yet." />
              ) : null}
            </div>
          </div>
        </section>
        </section>
      </main>
      <BottomSheet isOpen={crumbSheetOpen} onClose={() => setCrumbSheetOpen(false)} title="Checklist path">
        <div className="memberCrumbSheet">
          {breadcrumbSegments.map((segment) => {
            const kind = segment.kind.toLowerCase() as WorkspaceItemReference['kind']
            const reference = segment.targetId ? { id: segment.targetId, label: segment.label, kind, parentId: null, ancestors: [] } : null
            return <Pressable key={`${segment.kind}:${segment.label}`} disabled={!reference} onClick={() => { if (reference) onOpenWorkspaceItem?.(reference); setCrumbSheetOpen(false) }}><span>{segment.label}</span>{reference ? <ChevronRightRoundedIcon fontSize="small" /> : null}</Pressable>
          })}
        </div>
      </BottomSheet>
      {/* Member Capacitor app: every workspace-item selector uses WorkspaceItemPicker
          mobileHost="sheet". See docs/plans/checklist-detail-revamp.md §6. */}
      {pickerState?.field === 'comment' ? (
        <WorkspaceItemPicker
          open
          mobileHost="sheet"
          title="Attach workspace item"
          enabledKinds={['forms', 'designs', 'uploads', 'links']}
          mode="itemAttachment"
          forms={formsPage.items}
          designs={designsPage.items}
          uploads={workspaceFiles.map((file) => ({ id: file.id, name: file.fileName, title: file.fileName, sizeBytes: file.sizeBytes, mimeType: file.mimeType }))}
          links={[]}
          onSelect={selectWorkspaceItem}
          selectedIds={new Set([...attachedProvisionIds.forms, ...attachedProvisionIds.designs, ...attachedProvisionIds.uploads, ...attachedProvisionIds.links])}
          onClose={() => setPickerState(null)}
        />
      ) : pickerState ? (
        <WorkspaceItemPicker
          open
          mobileHost="sheet"
          title="Attach workspace item"
          enabledKinds={['forms', 'designs', 'uploads', 'links']}
          mode="itemAttachment"
          forms={formsPage.items}
          designs={designsPage.items}
          uploads={workspaceFiles.map((file) => ({ id: file.id, name: file.fileName, title: file.fileName, sizeBytes: file.sizeBytes, mimeType: file.mimeType }))}
          links={[]}
          multiSelect
          onCommitRefs={commitWorkspaceItems}
          selectedIds={new Set([...attachedProvisionIds.forms, ...attachedProvisionIds.designs, ...attachedProvisionIds.uploads, ...attachedProvisionIds.links])}
          onClose={() => setPickerState(null)}
        />
      ) : null}
      <AppDialog
        open={Boolean(pendingAssignee)}
        title="Apply this assignment"
        message="This checklist recurs. Choose which occurrences the new assignee should apply to."
        onDismiss={() => {
          setSelectedAssigneeId(item.assigneeId ?? '')
          setPendingAssignee(null)
        }}
        actions={[
          {
            label: 'Cancel',
            variant: 'ghost',
            onClick: () => {
              setSelectedAssigneeId(item.assigneeId ?? '')
              setPendingAssignee(null)
            },
          },
        ]}
      >
        <div className="memberChecklistScopeCard memberChecklistScopeCard--inDialog">
          <Pressable
            onClick={async () => {
              const p = pendingAssignee
              setPendingAssignee(null)
              if (p) await commitAssignment(p.assignee, 'single')
            }}
          >
            <strong>Only this occurrence</strong>
            <span>Others keep the current assignee.</span>
          </Pressable>
          <Pressable
            className="isPrimary"
            onClick={async () => {
              const p = pendingAssignee
              setPendingAssignee(null)
              if (p) await commitAssignment(p.assignee, 'future')
            }}
          >
            <strong>This and future occurrences</strong>
            <span>Past occurrences keep their current assignee.</span>
          </Pressable>
          <Pressable
            onClick={async () => {
              const p = pendingAssignee
              setPendingAssignee(null)
              if (p) await commitAssignment(p.assignee, 'global')
            }}
          >
            <strong>Every occurrence</strong>
            <span>Overrides every past and future instance.</span>
          </Pressable>
        </div>
      </AppDialog>
      <AppDialog
        open={notNeededScopeDialogOpen}
        title={t('NLSChecklistNotNeededTitle')}
        onDismiss={() => setNotNeededScopeDialogOpen(false)}
        actions={[
          {
            label: t('NLSChecklistNotNeededConfirm'),
            variant: 'primary',
            autoFocus: true,
            onClick: async () => {
              const scope = notNeededScopeChoice === 'FROM_PERIOD' ? 'FROM_PERIOD' : null
              setNotNeededScopeDialogOpen(false)
              await changeStatus('NOT_REQUIRED', { notRequiredScope: scope, skipScopePrompt: true })
            },
          },
          {
            label: 'Cancel',
            variant: 'ghost',
            onClick: () => setNotNeededScopeDialogOpen(false),
          },
        ]}
      >
        <SettingsRadioList
          name="member-not-needed-scope"
          value={notNeededScopeChoice}
          onChange={(val) => setNotNeededScopeChoice(val as 'THIS_PERIOD' | 'FROM_PERIOD')}
          options={
            item.checklistType === 'WEEKLY'
              ? [
                  { value: 'THIS_PERIOD', label: t('NLSChecklistNotNeededJustThisWeek') },
                  { value: 'FROM_PERIOD', label: t('NLSChecklistNotNeededFromThisWeekOn') },
                ]
              : [
                  { value: 'THIS_PERIOD', label: t('NLSChecklistNotNeededJustThisDay') },
                  { value: 'FROM_PERIOD', label: t('NLSChecklistNotNeededFromTodayOn') },
                ]
          }
        />
      </AppDialog>
      <AppDialog
        open={showDiscardDialog}
        title="Unsaved changes"
        message="You have unsaved changes. What would you like to do?"
        actions={[
          {
            label: 'Save',
            variant: 'primary',
            onClick: async () => {
              setShowDiscardDialog(false)
              const saved = await saveDetails()
              if (saved) {
                onClose()
              }
            },
          },
          {
            label: 'Discard',
            variant: 'danger',
            onClick: () => {
              setShowDiscardDialog(false)
              void editLock.release()
              onClose()
            },
          },
          {
            label: 'Cancel',
            onClick: () => {
              setShowDiscardDialog(false)
            },
          },
        ]}
        onDismiss={() => setShowDiscardDialog(false)}
      />
    </div>
    </ItemTimeProvider>
  )
}
